import { useCallback, useEffect, useState } from "react";
import { api, ApiError, debugMode, lastTrace,
         type Dimension, type ExploreResult, type Filter,
         type Property } from "../lib/api";
import { TimeChart, METRICS, type MetricKey } from "../components/TimeChart";
import { DateRangeSlider } from "../components/DateRangeSlider";
import { DebugPanel } from "../components/DebugPanel";
import { useDemo } from "../lib/demo";

const DIMENSIONS: { key: Dimension; label: string }[] = [
  { key: "query", label: "Queries" },
  { key: "page", label: "Pages" },
  { key: "country", label: "Countries" },
  { key: "device", label: "Devices" },
  { key: "appearance", label: "Search appearance" },
  { key: "date", label: "Dates" },
];

/**
 * The explorer. Deliberately the Search Console layout, because the point of
 * this screen is recognition: chart on top, dimension tabs, table below,
 * filters that accumulate as you drill.
 *
 * The differences are all where GSC hides something. Its table stops at 1,000
 * rows, its history stops at 16 months, and combining query with page
 * truncates further. None of that applies to the bulk export, so the row
 * count, the range and the withheld share are stated rather than glossed.
 */
export function Explore({ property, connectionId, onChangeSources }: {
  property: Property;
  connectionId: string | null;
  onChangeSources: () => void;
}) {
  const gsc = property.sources.find((s) => s.kind === "gsc_bq");
  const source = gsc?.bq_project && gsc?.bq_dataset
    ? { project: gsc.bq_project, dataset: gsc.bq_dataset, location: gsc.bq_location }
    : null;

  const [dimension, setDimension] = useState<Dimension>("query");
  const [filters, setFilters] = useState<Filter[]>([]);
  // The export's own bounds, not today's. Asking beyond them returns nothing
  // and makes the app look broken.
  const latest = gsc?.earliest_date ? isoOf(Date.now() - 3 * 86400000) : isoOf(Date.now() - 3 * 86400000);
  const earliest = gsc?.earliest_date ?? isoOf(Date.now() - 486 * 86400000);
  const [range, setRange] = useState(() => ({
    from: isoOf(Math.max(Date.parse(earliest), Date.now() - 90 * 86400000)),
    to: latest,
  }));
  const [selected, setSelected] = useState<MetricKey[]>(["clicks", "impressions"]);
  const [result, setResult] = useState<ExploreResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [sort, setSort] = useState<{ key: keyof ExploreResult["rows"][0]; desc: boolean }>(
    { key: "clicks", desc: true });
  const [search, setSearch] = useState("");
  const { demo } = useDemo();
  const [compare, setCompare] = useState(false);
  const [page, setPage] = useState(0);
  const PER_PAGE = 50;

  const { from, to } = range;
  const spanDays = Math.round(
    (Date.parse(to) - Date.parse(from)) / 86400000) + 1;
  // Daily points get unreadable past a few months, and cost more to render.
  const grain: "day" | "week" = spanDays > 120 ? "week" : "day";

  const run = useCallback(async () => {
    if (!demo && (!source || !connectionId)) return;
    setBusy(true); setError(null);
    try {
      const next = await api.explore({
        connection_id: connectionId ?? "",
        project: source?.project ?? "", dataset: source?.dataset ?? "",
        location: source?.location ?? null,
        from, to, dimension, filters, grain, limit: 1000,
        // Previous window of equal length, ending the day before this one.
        compare: compare ? {
          from: isoOf(Date.parse(from) - spanDays * 86400000),
          to: isoOf(Date.parse(from) - 86400000),
        } : null,
        demo,
      });
      setResult(next);
    } catch (e) {
      const err = e as ApiError;
      setFailure(err.body);
      setError(err.hint ?? `Query failed (${err.status}${err.code ? ` ${err.code}` : ""}).`);
    } finally { setBusy(false); }
  }, [connectionId, source?.project, source?.dataset, dimension,
      JSON.stringify(filters), from, to, grain, compare, spanDays, demo]);

  // A new selection invalidates the page you were on, but not your sort.
  useEffect(() => { setPage(0); }, [dimension, JSON.stringify(filters), from, to, search]);

  useEffect(() => { void run(); }, [run]);

  function drill(key: string) {
    // Clicking a row filters to it and moves to the next dimension, the way
    // GSC does when you click a query and then open the Pages tab.
    if (dimension === "date") return;
    const next: Dimension = dimension === "query" ? "page" : "query";
    setFilters((f) => [...f, { dim: dimension, op: "is", value: key }]);
    setDimension(next);
  }

  // Sample data needs no source, which is the point: the tools stay usable
  // before an export exists.
  if (!source && !demo) {
    return (
      <div className="shell">
        <div className="warning">
          {property.name} has no Search Console export selected.
        </div>
        <button onClick={onChangeSources}>Pick sources</button>
      </div>
    );
  }

  const rows = (result?.rows ?? [])
    .filter((r) => !search || r.key.toLowerCase().includes(search.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      const cmp = typeof av === "string"
        ? String(av).localeCompare(String(bv))
        : Number(av ?? 0) - Number(bv ?? 0);
      return sort.desc ? -cmp : cmp;
    });

  const withheldShare = result && result.totals.impressions
    ? result.totals.withheldImpressions / result.totals.impressions
    : 0;

  return (
    <div className="shell wide">
      <div className="ex-bar">
        <h1>{property.name}</h1>
        <div className="ex-bar-right">
          <label className="ex-compare">
            <input type="checkbox" checked={compare}
                   onChange={(e) => setCompare(e.target.checked)} />
            Compare to previous {spanDays} days
          </label>
          <button className="quiet" onClick={onChangeSources}>Sources</button>
        </div>
      </div>

      <DateRangeSlider
        min={earliest}
        max={latest}
        from={from}
        to={to}
        onChange={(f, t) => setRange({ from: f, to: t })}
      />

      <div className="chips">
        {filters.map((f, i) => (
          <button key={`${f.dim}-${f.value}-${i}`} className="chip"
                  title={`Remove filter: ${f.value}`}
                  onClick={() => setFilters((s) => s.filter((_, j) => j !== i))}>
            <span className="chip-dim">{dimLabel(f.dim)}</span>
            <span className="chip-val">{shortValue(f.value)}</span>
            <span className="chip-x" aria-hidden="true">×</span>
          </button>
        ))}
        {filters.length > 0 && (
          <button className="chip clear" onClick={() => setFilters([])}>Clear all</button>
        )}
      </div>

      {error && <div className="warning">{error}</div>}

      {result && (
        <div className={busy ? "stale" : undefined} aria-busy={busy}>
          <TimeChart
            result={result}
            selected={selected}
            onToggle={(k) => setSelected((s) =>
              s.includes(k) ? (s.length > 1 ? s.filter((x) => x !== k) : s) : [...s, k])}
          />

          <div className="ex-tabs">
            {DIMENSIONS.map((d) => (
              <button key={d.key} className={d.key === dimension ? "on" : ""}
                      disabled={busy}
                      onClick={() => setDimension(d.key)}>
                {d.label}
              </button>
            ))}
            <input className="ex-search" placeholder="Filter rows"
                   value={search} onChange={(e) => setSearch(e.target.value)} />
            <button className="ex-export" disabled={!rows.length}
                    title="Download the rows below as CSV"
                    onClick={() => downloadCsv(dimension, rows)}>
              Export
            </button>
          </div>

          <div className="tbl-wrap">
            <table className="tbl ex-tbl">
              <thead>
                <tr>
                  <th className="ex-key">{DIMENSIONS.find((d) => d.key === dimension)?.label}</th>
                  {METRICS.map((m) => (
                    <th key={m.key} className="r"
                        onClick={() => setSort((s) =>
                          s.key === m.key ? { key: m.key, desc: !s.desc } : { key: m.key, desc: true })}>
                      {m.label.replace("Total ", "").replace("Average ", "")}
                      <span className="hdr-arrow">
                        {sort.key === m.key ? (sort.desc ? " ↓" : " ↑") : " ↕"}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {busy && !rows.length && (
                  <tr><td colSpan={5} className="ex-loading">Querying BigQuery</td></tr>
                )}
                {rows.slice(page * PER_PAGE, (page + 1) * PER_PAGE).map((r) => (
                  <tr key={r.key}
                      className={dimension === "date" ? "" : "expandable"}
                      tabIndex={dimension === "date" ? undefined : 0}
                      onClick={() => drill(r.key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); drill(r.key); }
                      }}>
                    <td className="cell-text ex-key" title={r.key}>{r.key}</td>
                    {METRICS.map((m) => {
                      const prevKey = `prev${m.key[0].toUpperCase()}${m.key.slice(1)}` as
                        "prevClicks" | "prevImpressions" | "prevCtr" | "prevPosition";
                      const was = r[prevKey];
                      const now = r[m.key];
                      const delta = compare && was != null && now != null && was !== 0
                        ? (now - was) / Math.abs(was) : null;
                      const good = delta == null ? null
                        : m.key === "position" ? delta < 0 : delta > 0;
                      return (
                        <td key={m.key} className="r num">
                          {m.fmt(now)}
                          {delta != null && (
                            <span className={`cell-delta ${good ? "up" : "down"}`}>
                              {delta > 0 ? "+" : ""}{(delta * 100).toFixed(0)}%
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {!busy && !rows.length && (
                  <tr>
                    <td colSpan={5} className="ex-empty">
                      {dimension === "query" && withheldShare > 0.99 ? (
                        <>
                          <strong>Every impression in this selection had its query withheld.</strong>
                          <span>
                            Google anonymizes rare queries to protect user privacy, and on a
                            small selection that can be all of them. The clicks and impressions
                            above are real; only the terms behind them are hidden. Widen the
                            date range or remove a filter to see queries.
                          </span>
                        </>
                      ) : search ? (
                        <>
                          <strong>Nothing matches "{search}".</strong>
                          <span>Clear the row filter to see all {result?.rows.length ?? 0} rows.</span>
                        </>
                      ) : (
                        <>
                          <strong>No data in this selection.</strong>
                          <span>Try a wider date range or fewer filters.</span>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {rows.length > PER_PAGE && (
            <div className="pager">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span className="num">
                {(page * PER_PAGE + 1).toLocaleString()}–
                {Math.min((page + 1) * PER_PAGE, rows.length).toLocaleString()}
                {" of "}{rows.length.toLocaleString()}
              </span>
              <button disabled={(page + 1) * PER_PAGE >= rows.length}
                      onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          )}

          <div className="ex-foot">
            <span>
              <strong className="num">{result.totals.distinct.toLocaleString()}</strong>{" "}
              {dimension === "query" ? "named queries" : "rows"} in this selection
              {result.truncated && `, of which the top ${result.rows.length.toLocaleString()} were fetched`}.
            </span>
            {dimension === "query" && withheldShare > 0.01 && (
              <span className="ex-foot-note">
                {(withheldShare * 100).toFixed(0)}% of impressions had the query
                withheld ({result.totals.withheldImpressions.toLocaleString()}).
                Search Console drops these without saying so.
              </span>
            )}
            <span className="ex-foot-cost num">
              {result.demo
                ? "sample data · not your property"
                : result.cached
                ? "cached · no query run"
                : `${(result.bytes / 1024 ** 2).toFixed(1)} MB scanned · $${result.estUsd.toFixed(4)}`}
            </span>
          </div>
        </div>
      )}

      {!result && busy && (
        <div className="ex-first-load">
          <span className="spinner" /> Loading {property.name}
        </div>
      )}

      {(debugMode() || failure !== null) && (
        <DebugPanel trace={lastTrace} error={failure ?? undefined} />
      )}
    </div>
  );
}

function dimLabel(d: Dimension): string {
  return DIMENSIONS.find((x) => x.key === d)?.label.replace(/s$/, "") ?? d;
}

/** Chips must stay one line. Keep the identifying end of a URL, not the host. */
function shortValue(v: string): string {
  const bare = v.replace(/^https?:\/\//, "").replace(/\/$/, "");
  if (bare.length <= 42) return bare;
  return `…${bare.slice(-40)}`;
}

const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);

/** CSV of what is on screen, filters and sort included, not the raw response. */
function downloadCsv(dimension: string, rows: ExploreResult["rows"]) {
  const esc = (v: unknown) => {
    const str = String(v ?? "");
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  const csv = [
    [dimension, "clicks", "impressions", "ctr", "position"].join(","),
    ...rows.map((r) => [
      esc(r.key), r.clicks, r.impressions,
      r.ctr == null ? "" : (r.ctr * 100).toFixed(2),
      r.position == null ? "" : r.position.toFixed(1),
    ].join(",")),
  ].join("\n");

  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${dimension}-${isoOf(Date.now())}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
